import React, {useCallback, useState} from 'react';
import Department from 'records/Department';
import Team from 'records/Team';
import {List} from 'immutable';
import User from 'records/User';
import Dialog from '@material-ui/core/Dialog';
import {ClosableDialogTitle} from '@components/dialog/closable-title/ClosableDialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';
import Autocomplete from '@material-ui/lab/Autocomplete';
import {IdMap} from '@store/@types/util';
import TeamActions from 'actions/TeamActions';
import {TrashIcon} from '@components/icon/TrashIcon';
import './AdminTeamDialog.sass';

interface AdminTeamDialogProps {
  departments: List<Department>;
  users: IdMap<User>;
  team?: Team;
  onClose: () => void;
}

export const AdminTeamDialog: React.FC<AdminTeamDialogProps> = ({
  departments,
  users,
  team,
  onClose,
}) => {
  const [manager, setManager] = useState(users.get(team?.managerId) ?? null);
  const [department, setDepartment] = useState(
    departments.find(d => d.id === team?.departmentId) ?? null
  );
  const [teamName, setTeamName] = useState(team?.name ?? '');
  const [submitting, setSubmitting] = useState(false);

  const submit = useCallback(async () => {
    if (!manager || !department || !teamName || submitting) return;
    setSubmitting(true);

    try {
      const newTeam = new Team(team ? team.toJS() : {})
        .set('managerId', manager.id)
        .set('departmentId', department.id)
        .set('name', teamName);
      if (team) {
        await TeamActions.updateTeam(newTeam);
      } else {
        await TeamActions.createTeam(newTeam);
      }
      onClose();
    } catch (e) {
      console.error(e);
      setSubmitting(false);
    }
  }, [manager, department, teamName, submitting, setSubmitting, team, onClose]);

  const deleteTeam = useCallback(async () => {
    if (submitting || !team) return;
    try {
      await TeamActions.deleteTeam(team.id);
      onClose();
    } catch (e) {
      console.error(e);
      setSubmitting(false);
    }
  }, [team, submitting, onClose, setSubmitting]);

  return (
    <Dialog open className="admin-team-dialog">
      <ClosableDialogTitle onClose={onClose}>{team ? 'Edit' : 'Add'} Team</ClosableDialogTitle>
      <DialogContent className="fields">
        <div className="field">
          <label>Team Name</label>
          <TextField
            className="field-input"
            variant="outlined"
            size="small"
            value={teamName}
            onChange={e => setTeamName(e.target.value)}
          />
        </div>
        <div className="field">
          <label>Department</label>
          <Autocomplete
            className="field-input"
            autoHighlight
            options={departments.toArray()}
            value={department}
            getOptionLabel={d => d.name}
            getOptionSelected={(opt, val) => opt.id === val.id}
            renderInput={params => <TextField {...params} variant="outlined" size="small" />}
            onChange={(e, v) => setDepartment(v)}
          />
        </div>
        <div className="field">
          <label>Manager</label>
          <Autocomplete
            className="field-input"
            autoHighlight
            options={users.toList().toArray()}
            value={manager}
            getOptionLabel={u => u.getFullName()}
            getOptionSelected={(opt, val) => opt.id === val.id}
            renderInput={params => <TextField {...params} variant="outlined" size="small" />}
            onChange={(e, v) => setManager(v)}
          />
        </div>
      </DialogContent>
      <DialogActions className="actions">
        {team?.usersCount === 0 ? (
          <Button disabled={submitting} className="delete-button" onClick={deleteTeam}>
            <TrashIcon height={16} /> Delete Team
          </Button>
        ) : (
          <div />
        )}
        <div className="submit-cancel-wrapper">
          <Button variant="outlined" onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={!manager || !department || !teamName || submitting}
            variant="contained"
            color="primary"
            onClick={submit}>
            Save
          </Button>
        </div>
      </DialogActions>
    </Dialog>
  );
};
